// ═══════════════════════════════════════════════════════════════════════════
// pendingEpisodeStore.js — survives the apex → app.meetepio.com hop.
//
// When an anonymous visitor finishes the landing-chat episode preview,
// the locked document is stashed here. After signup the apex
// → app.meetepio.com redirect fires; on the app subdomain we read this
// back and auto-claim the episode under the new user's account so the
// editor opens straight to their work — no "where did my episode go?"
// moment.
//
// Storage strategy:
//   • Primary: cookie scoped to `.meetepio.com`, chunked across N
//     cookies so payloads over the ~4 KB per-cookie browser limit
//     still survive. Cookie domain is shared on apex + app subdomain.
//   • Mirror:  localStorage on the writing origin. Lets dev / legacy
//     hosts that don't share a `.meetepio.com` cookie continue to
//     work via same-origin storage.
//   • Reader: cookie first (cross-domain); falls back to localStorage
//     (same-origin) when no cookie is found.
//
// Short expiry (15 min) so a stale handoff from a previous session
// can't resurface as "claim this episode" on a future cold visit.
// ═══════════════════════════════════════════════════════════════════════════

import { LANDING_HOST, isMeetepioHost } from './hosts';

const KEY = 'epio_pending_episode_v1';
const COOKIE_DOMAIN = `.${LANDING_HOST}`;
const CHUNK_SIZE    = 3200;     // safe headroom under the 4 KB cookie cap
const MAX_CHUNKS    = 30;       // ~96 KB ceiling; episode docs are ~5–30 KB
const TTL_MS        = 15 * 60 * 1000;

function readRawCookie(name) {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() !== name) continue;
    return part.slice(eq + 1);
  }
  return null;
}

function writeRawCookie(name, encodedValue, expiresMs) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + expiresMs).toUTCString();
  document.cookie =
    `${name}=${encodedValue}; Path=/; Domain=${COOKIE_DOMAIN}; ` +
    `Expires=${expires}; SameSite=Lax; Secure`;
}

function deleteRawCookie(name) {
  if (typeof document === 'undefined') return;
  document.cookie =
    `${name}=; Path=/; Domain=${COOKIE_DOMAIN}; ` +
    `Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

function deleteChunks(name) {
  for (let i = 0; i < MAX_CHUNKS; i++) {
    const k = `${name}.${i}`;
    if (readRawCookie(k) == null) break;
    deleteRawCookie(k);
  }
}

function readCookieValue(name) {
  const single = readRawCookie(name);
  if (single != null) {
    try { return decodeURIComponent(single); }
    catch { return null; }
  }
  let assembled = '';
  for (let i = 0; i < MAX_CHUNKS; i++) {
    const part = readRawCookie(`${name}.${i}`);
    if (part == null) break;
    assembled += part;
  }
  if (!assembled) return null;
  try { return decodeURIComponent(assembled); }
  catch { return null; }
}

function writeCookieValue(name, value) {
  const encoded = encodeURIComponent(value);
  if (encoded.length <= CHUNK_SIZE) {
    writeRawCookie(name, encoded, TTL_MS);
    deleteChunks(name);
    return;
  }
  deleteRawCookie(name);
  const total = Math.ceil(encoded.length / CHUNK_SIZE);
  for (let i = 0; i < total && i < MAX_CHUNKS; i++) {
    const slice = encoded.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    writeRawCookie(`${name}.${i}`, slice, TTL_MS);
  }
  for (let i = total; i < MAX_CHUNKS; i++) {
    const k = `${name}.${i}`;
    if (readRawCookie(k) == null) break;
    deleteRawCookie(k);
  }
}

export function setPendingEpisode(payload) {
  const serialized = JSON.stringify(payload);
  // Mirror to localStorage on the writing origin so dev / legacy hosts
  // that don't share the meetepio.com cookie keep working same-origin.
  try { localStorage?.setItem(KEY, serialized); } catch {}
  if (isMeetepioHost()) {
    try { writeCookieValue(KEY, serialized); } catch {}
  }
}

export function getPendingEpisode() {
  // Cookie first — survives cross-subdomain navigation.
  if (isMeetepioHost()) {
    const fromCookie = readCookieValue(KEY);
    if (fromCookie) {
      try { return JSON.parse(fromCookie); }
      catch { /* fall through to localStorage */ }
    }
  }
  try {
    const raw = localStorage?.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearPendingEpisode() {
  try { localStorage?.removeItem(KEY); } catch {}
  if (isMeetepioHost()) {
    deleteRawCookie(KEY);
    deleteChunks(KEY);
  }
}
